import { defineComponent as _defineComponent } from 'vue'

const __default__ = {
    inheritAttrs: false,
};

import 'bootstrap';

import { Portal } from '@linusborg/vue-simple-portal';
import $ from 'jquery';
import {
    computed,
    inject, nextTick, onMounted, provide, ref, shallowRef, unref, watch,
} from 'vue';

import { errutil } from '#public/commons-global';
import type { NoShowError } from '#public_ts/commons';

import { useGenerateIds, useJqueryEventListener } from '../composables';
import { type DefaultModalDialogApi, ModalCancelBehavior } from './bootstrap-modal';
import { PortalPin } from './injection';

type ModalSize = 'modal-sm' | 'modal-lg' | 'modal-xxl' | 'modal-fullscreen';
//valueProp, it cannot handle dynamic ones

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'bootstrap-modal',
  props: {
    title: null,
    ok: { type: [String, Boolean] },
    oktype: null,
    cancel: { type: [String, Boolean] },
    close: { type: [String, Boolean] },
    size: null,
    static: { type: Boolean },
    disabled: { type: Boolean },
    modelValue: { type: Boolean },
    pin: { type: Boolean },
    formClass: null,
    parentForm: { type: Boolean }
  },
  emits: ["update:modelValue", "form-submit", "dialog-opened", "dialog-closed", "after-submit"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'update:modelValue', value: boolean | undefined): void;
    (event: 'form-submit', value: undefined): void;
    (event: 'dialog-opened', value: undefined): void;
    (event: 'dialog-closed', value: undefined): void;
    (event: 'after-submit', value: undefined): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    title?: string;
    ok?: string | boolean;
    oktype?: 'submit' | 'button';
    cancel?: string | boolean;
    close?: string | boolean;
    size?: ModalSize;
    static?: boolean;
    disabled?: boolean;
    modelValue?: boolean;
    pin?: boolean;
    formClass?: string;
    parentForm?: boolean;
}

const generateIds = useGenerateIds();
const labelId = generateIds();

//modelValue + update:modelValue;

//eslint-disable-next-line no-spaced-func


const showModal = ref<boolean>(false);
const transition = ref(false);
let onBeforeSubmit: (() => Promise<boolean>) | undefined;
const modalSubmitted = ref(false);
const modalElement = shallowRef<HTMLElement | undefined>();
const hasForm = ref(true);
const hasOk = computed(() => !((props.ok === false) || (props.ok === undefined)));
const hasCancel = computed(() => !((props.cancel === false) || (props.cancel === undefined)));
const portalPin = inject(PortalPin, undefined);
if(portalPin !== undefined) {
    //this fix for modals inside modals, so they all go to separate portal
    provide(PortalPin, undefined);
}

const modalApi: DefaultModalDialogApi<unknown> = {
    toggleModal(_showModal) {
        return Promise.resolve()
            .then(() => {
                updateShowModal(_showModal);
            })
            .then(() => {
                waitCurrentTransition();
            });
    },
    requestSubmit,
    isSubmitted: computed(() => unref(modalSubmitted)),
    showModal(options) {
        let submitResult: unknown;
        if(onBeforeSubmit) return Promise.reject(new Error('Already waiting for dialog'));
        onBeforeSubmit = () => Promise.resolve()
            .then(() => {
                const _onBeforeSubmit = options?.onBeforeSubmit;
                return _onBeforeSubmit?.();
            })
            .then((_submitResult) => {
                submitResult = _submitResult;
                updateShowModal(false);
                return true;
            })
            .catch((err: NoShowError) => {
                if(err && !err.no_show_error) errutil.box(null, err);
                return false;
            });

        return new Promise((resolve, reject) => {
            const _unwatch = watch(showModal, () => {
                Promise.resolve()
                    .then(() => waitCurrentTransition())
                    .then(() => {
                        if(unref(showModal)) return;
                        _unwatch();
                        onBeforeSubmit = undefined;
                        if(unref(modalSubmitted)) {
                            resolve(submitResult);
                            return;
                        }
                        const onCancel = options?.onCancel ?? ModalCancelBehavior.NoReturn;
                        switch(onCancel) {
                            case ModalCancelBehavior.Resolve:
                                resolve(undefined);
                                break;
                            case ModalCancelBehavior.Reject:
                                reject(new Error('Cancelled'));
                                break;
                        }
                    });
            });
            updateShowModal(true);
        });
    },
};
expose(modalApi);

function waitCurrentTransition() {
    return new Promise((resolve) => {
        if(!unref(transition)) {
            resolve(undefined);
            return;
        }
        const _unwatch = watch(transition, () => {
            // multi-entry support, transition may be modified by other waiter
            // then we wait another transition
            if(unref(transition)) return;
            _unwatch();
            resolve(undefined);
        });
    });
}

function updateShowModal(_showModal: boolean | undefined) {
    const _showModal0 = !!_showModal;
    const _modalElement = unref(modalElement);
    if(!_modalElement) return Promise.resolve();

    return Promise.resolve()
        .then(() => waitCurrentTransition())
        .then(() => {
            if(_showModal0 === unref(showModal)) return;

            transition.value = true;
            showModal.value = _showModal0;
            if(!_showModal0) {
                $(_modalElement).modal('hide');
            } else {
                modalSubmitted.value = false;
                $(_modalElement).modal('show');
            }
        });
}

function requestSubmit() {
    Promise.resolve()
        .then(() => onBeforeSubmit?.())
        .then((submitResult) => {
            if(submitResult === false) return;
            modalSubmitted.value = true;
            emit('form-submit', undefined);
        });
}

function tyrRequestSubmit() {
    if(!props.parentForm) {
        requestSubmit();
    }
    // form will submit
}

function processModalToggle(_showModal: boolean | undefined) {
    const _showModal0 = !!_showModal;
    nextTick(() => {
        if(_showModal0 !== unref(showModal)) {
            // warning, properties not in sync, see updateShowModal
            showModal.value = _showModal0;
        }
        if(!!props.modelValue !== _showModal0) {
            emit('update:modelValue', _showModal0);
        }
        transition.value = false;
        if(_showModal0) {
            emit('dialog-opened', undefined);
        } else {
            emit('dialog-closed', undefined);
            if(unref(modalSubmitted)) {
                emit('after-submit', undefined);
            }
        }
    });
}

watch(() => props.modelValue, (val, old) => {
    if(!!val !== !!old) {
        updateShowModal(val);
    }
});

onMounted(() => {
    //portal requires next tick
    nextTick(() => {
        const _modalElement = unref(modalElement);
        if(_modalElement) {
            hasForm.value = $(_modalElement).closest('form').length > 0;
        }

        if(props.modelValue) {
            updateShowModal(true);
        }
    });
});

useJqueryEventListener(modalElement, 'shown.bs.modal', () => {
    modalSubmitted.value = false;
    processModalToggle(true);
});
useJqueryEventListener(modalElement, 'hide.bs.modal', () => {
    transition.value = true;
});
useJqueryEventListener(modalElement, 'hidden.bs.modal', () => {
    //model value is updated at this point
    processModalToggle(false);
});

return { __sfc: true,generateIds, labelId, props, emit, showModal, transition, onBeforeSubmit, modalSubmitted, modalElement, hasForm, hasOk, hasCancel, portalPin, modalApi, waitCurrentTransition, updateShowModal, requestSubmit, tyrRequestSubmit, processModalToggle, Portal }
}

})